'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './IsometricShowcase.module.css';

/**
 * Card artwork must be the FLAT (un-projected) face — this component applies
 * the isometric projection itself. Overridable via the `cards` prop.
 */
export const DEFAULT_CARDS = [
  {
    src: '/images/showcase/quickvee-brand.webp',
    alt: 'Quickvee brand card',
    sideColor: '#1A1A6B',
  },
  {
    src: '/images/showcase/quickvee-dashboard.webp',
    alt: 'Quickvee merchant dashboard',
    sideColor: '#3A3A8C',
  },
  {
    src: '/images/showcase/quickvee-website.jpg',
    alt: 'Quickvee customer storefront',
    sideColor: '#4A4AA0',
  },
  {
    src: '/images/showcase/quickvee-pos.webp',
    alt: 'Quickvee POS manager tablet',
    sideColor: '#0D0D1F',
  },
];

/**
 * Static channel chips. Coordinates are percentages of the field box (the tight
 * wrapper around deck + lifted ghost); `x`/`y` is the chip's slab-face centre.
 *
 * Measured off the reference layout: chip faces solved from their bboxes
 * (466px square + 83px extrude on the 3636x2527 canvas), and each link
 * extended along its ±30deg axis to the card edge it actually lands on.
 *
 * A link runs `from` -> `to`. It does NOT start at the chip centre: in the
 * reference each line leaves a chip corner, so the centre is off-axis and a
 * centre-anchored line comes out at the wrong angle (Website was 37deg instead
 * of 30deg). `fromX`/`fromY` is the chip centre projected onto the ±30deg axis
 * through `toX`/`toY`, which keeps every line exactly on-axis while still
 * starting under the slab, where it is hidden.
 *
 * `step` ties a chip to the card it labels, as an index into the rendered deck
 * — [brand, dashboard, storefront, pos, brand]. Its connector line draws while
 * that card slides down, so the line reads as "this screen just plugged in".
 * Omit `step` and the line simply draws during the intro instead.
 */
export const DEFAULT_CHIPS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    step: 1,
    x: 13.23,
    y: 88.4,
    fromX: 13.17,
    fromY: 88.28,
    toX: 32.11,
    toY: 76.01,
  },
  {
    id: 'website',
    label: 'Website',
    step: 2,
    x: 21.6,
    y: 37.71,
    fromX: 20.44,
    fromY: 39.97,
    toX: 36.89,
    toY: 50.63,
  },
  {
    id: 'pos',
    label: 'POS',
    step: 3,
    x: 86.78,
    y: 82.25,
    fromX: 86.37,
    fromY: 83.06,
    toX: 66.25,
    toY: 70.02,
  },
];

/**
 * Every chip label renders at the SAME size — mixed sizes read as a mistake.
 * That size is whatever the longest label ("Dashboard") can take without
 * overflowing its square slab.
 *
 * The length-based estimate below is only a starting point: the uppercase
 * advance ratio swings ~0.64-0.73 with the letters and the font that actually
 * resolves, so the size is corrected against real measured text widths after
 * mount. The correction only has to run once — font-size and slab width are
 * both proportional to the container, so the fill ratio is scale-invariant.
 */
const CHIP_FONT_CAP = 0.34;
const CHIP_FILL = 0.92;
const chipFontScale = (label) =>
  Math.min(
    CHIP_FONT_CAP,
    CHIP_FILL / (Math.max(1, String(label).length) * 0.66),
  );
const uniformChipFontScale = (chips) =>
  chips.length
    ? Math.min(...chips.map((chip) => chipFontScale(chip.label)))
    : CHIP_FONT_CAP;

const BACKGROUND_CLASS = {
  transparent: styles.backgroundTransparent,
  light: styles.backgroundLight,
  dark: styles.backgroundDark,
};
const BACKGROUND_ORDER = ['transparent', 'light', 'dark'];
const STATIC_POSTER_SRC = '/images/partner-platform-commerce.webp';
const STATIC_POSTER_BACKUP_SRC = '/images/partner-pos.png';
const STATIC_POSTER_ALT = 'Quickvee connected retail platform overview';
const IMAGE_PRELOAD_TIMEOUT_MS = 3000;
const DEFERRED_PRELOAD_FALLBACK_MS = 3500;

/*
 * Beat sheet measured frame-by-frame off the reference render (11.00s, 30fps):
 * intro 0.10-0.75, settles at 0.75 / 3.70 / 5.70 / 7.70 / 9.70, ghost appears
 * 0.95s before each slide, slide lasts 0.55s, outro 10.20-10.80, then empty.
 * The deck returns to the first card before fading, so N cards = N+1 steps.
 */
const BASE_DURATION = 11000;
const BASE = {
  introEmpty: 100,
  introIn: 650,
  firstHold: 1450,
  hold: 500,
  ghost: 950,
  slide: 550,
  lastHold: 500,
  outro: 600,
  outroEmpty: 200,
  /* Only used by chips with no `step` of their own — see DEFAULT_CHIPS. */
  linkDraw: 480,
  linkStagger: 130,
};
/* The reference render settles without overshoot; raise to ~1.05 to reinstate
 * the springier settle if you prefer it. */
const OVERSHOOT = 1;
const SETTLE_MS = 120;
/* Ghost drifts down ~14% of the lift while it is on screen. */
const GHOST_DRIFT = 0.14;

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Newton-solved cubic-bezier, so JS easing matches a CSS curve exactly. */
const cubicBezier = (x1, y1, x2, y2) => {
  const ca = (a, b) => 1 - 3 * b + 3 * a;
  const cb = (a, b) => 3 * b - 6 * a;
  const cc = (a) => 3 * a;
  const calc = (t, a, b) => ((ca(a, b) * t + cb(a, b)) * t + cc(a)) * t;
  const slope = (t, a, b) => 3 * ca(a, b) * t * t + 2 * cb(a, b) * t + cc(a);

  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 6; i += 1) {
      const s = slope(t, x1, x2);
      if (s === 0) break;
      t -= (calc(t, x1, x2) - x) / s;
    }
    return calc(t, y1, y2);
  };
};

const easeScaleIn = cubicBezier(0.2, 0.8, 0.2, 1);
/* Fitted to the reference descent: y travels ~70% in the first third. */
const easeSlide = cubicBezier(0.15, 0.75, 0.25, 1);
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;

const slideProgress = (elapsed, slideMs, settleMs) => {
  if (OVERSHOOT === 1) return easeSlide(clamp01(elapsed / slideMs));
  const main = Math.max(1, slideMs - settleMs);
  if (elapsed <= main) return OVERSHOOT * easeSlide(clamp01(elapsed / main));
  const s = clamp01((elapsed - main) / Math.max(1, settleMs));
  return OVERSHOOT + (1 - OVERSHOOT) * easeInOutSine(s);
};

const buildTimeline = (stepCount, duration) => {
  const k = duration / BASE_DURATION;
  const seg = Object.keys(BASE).reduce((acc, key) => {
    acc[key] = BASE[key] * k;
    return acc;
  }, {});
  seg.settle = SETTLE_MS * k;

  const transitions = Math.max(0, stepCount - 1);
  const steps = [];
  let settled = seg.introEmpty + seg.introIn;
  steps.push({ ghostStart: null, slideStart: null, settled });

  for (let i = 1; i <= transitions; i += 1) {
    const hold = i === 1 ? seg.firstHold : seg.hold;
    const ghostStart = settled + hold;
    const slideStart = ghostStart + seg.ghost;
    settled = slideStart + seg.slide;
    steps.push({ ghostStart, slideStart, settled });
  }

  return {
    seg,
    steps,
    introStart: seg.introEmpty,
    introEnd: seg.introEmpty + seg.introIn,
    outroStart: settled + seg.lastHold,
    outroEnd: settled + seg.lastHold + seg.outro,
  };
};

const IsometricShowcase = ({
  cards = DEFAULT_CARDS,
  chips = DEFAULT_CHIPS,
  duration = BASE_DURATION,
  autoPlay = true,
  showControls = false,
  background = 'transparent',
  className,
  onCycleComplete,
}) => {
  const safeCards = cards && cards.length ? cards : DEFAULT_CARDS;
  const safeChips = chips || [];
  const safeDuration =
    Number.isFinite(duration) && duration > 0 ? duration : BASE_DURATION;

  // The deck cycles back to the opening card before fading out.
  const steps = useMemo(
    () => (safeCards.length > 1 ? [...safeCards, safeCards[0]] : safeCards),
    [safeCards],
  );

  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const slotRefs = useRef([]);
  const ghostRefs = useRef([]);
  const chipLabelRefs = useRef([]);
  const linkRefs = useRef([]);
  const linkOffsets = useRef([]);
  const scrubRef = useRef(null);

  const rafRef = useRef(null);
  const lastTsRef = useRef(null);
  const elapsedRef = useRef(0);
  const cycleRef = useRef(0);
  const onCycleCompleteRef = useRef(onCycleComplete);

  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [activeIndex, setActiveIndex] = useState(0);
  const [bg, setBg] = useState(background);
  const [imagesReady, setImagesReady] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [inView, setInView] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);

  useEffect(() => {
    onCycleCompleteRef.current = onCycleComplete;
  }, [onCycleComplete]);

  useEffect(() => setBg(background), [background]);
  useEffect(() => setIsPlaying(autoPlay), [autoPlay]);

  const timeline = useMemo(
    () => buildTimeline(steps.length, safeDuration),
    [steps.length, safeDuration],
  );

  // ---- browser-only side effects (SSR safe: nothing above touches window) ----

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const sync = () => setTabVisible(document.visibilityState !== 'hidden');
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '80px 0px', threshold: 0.05 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Fetch animation faces after critical page loading, at low priority.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let cancelled = false;
    let started = false;
    let preloadTimeoutId;
    let deferredStartId;
    let loaders = [];

    setImagesReady(false);
    setImageLoadFailed(false);

    const startPreload = () => {
      if (cancelled || started) return;
      started = true;

      let pending = safeCards.length;
      if (!pending) {
        setImagesReady(true);
        return;
      }

      let failed = false;
      preloadTimeoutId = window.setTimeout(() => {
        if (cancelled) return;
        failed = true;
        setImageLoadFailed(true);
      }, IMAGE_PRELOAD_TIMEOUT_MS);

      const done = (didLoad) => {
        if (cancelled) return;
        if (!didLoad) {
          failed = true;
          setImageLoadFailed(true);
        }
        pending -= 1;
        if (pending > 0) return;

        window.clearTimeout(preloadTimeoutId);
        if (!failed) setImagesReady(true);
      };

      loaders = safeCards.map((card) => {
        const img = new window.Image();
        img.decoding = 'async';
        img.fetchPriority = 'low';
        img.onload = () => done(true);
        img.onerror = () => done(false);
        img.src = card.src;
        return img;
      });
    };

    if (document.readyState === 'complete') {
      startPreload();
    } else {
      window.addEventListener('load', startPreload, { once: true });
      deferredStartId = window.setTimeout(
        startPreload,
        DEFERRED_PRELOAD_FALLBACK_MS,
      );
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', startPreload);
      window.clearTimeout(deferredStartId);
      window.clearTimeout(preloadTimeoutId);
      loaders.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [safeCards]);

  // Shrink any chip label that still overflows its slab at the estimated size.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    let cancelled = false;

    const fitLabels = () => {
      if (cancelled) return;
      const nodes = chipLabelRefs.current.filter(Boolean);
      if (!nodes.length) return;

      const base = uniformChipFontScale(safeChips);
      const apply = (value) =>
        nodes.forEach((node) =>
          node.style.setProperty(
            '--iso-chip-font',
            `calc(var(--iso-chip-size) * ${value})`,
          ),
        );

      apply(base);

      // One shared shrink factor, driven by whichever label overflows most.
      let scale = 1;
      nodes.forEach((node) => {
        const slab = node.parentElement;
        if (!slab || !slab.clientWidth) return;
        const available = slab.clientWidth * CHIP_FILL;
        const measured = node.scrollWidth;
        if (measured > available) scale = Math.min(scale, available / measured);
      });

      if (scale < 1) apply((base * scale).toFixed(4));
    };

    fitLabels();
    // Web fonts land after first paint and change the metrics.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitLabels).catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [safeChips]);

  /*
   * Link end points are driven imperatively (see applyFrame) and deliberately
   * kept out of JSX: React re-applies inline attributes on every re-render,
   * which would snap a half-drawn line back. Seed them here — collapsed
   * normally, then animated to their destination by applyFrame.
   */
  useEffect(() => {
    linkRefs.current.forEach((node, index) => {
      const chip = safeChips[index];
      if (!node || !chip) return;
      node.setAttribute('x2', String(chip.fromX));
      node.setAttribute('y2', String(chip.fromY));
      linkOffsets.current[index] = null;
    });
  }, [safeChips]);

  // ---- frame writer: pure DOM mutation, never setState per frame ----

  const applyFrame = useCallback(
    (time) => {
      const {
        steps: schedule,
        seg,
        introStart,
        introEnd,
        outroStart,
        outroEnd,
      } = timeline;

      const stage = stageRef.current;
      if (stage) {
        let scale = 1;
        let opacity = 1;
        if (time < introStart) {
          scale = 0.4;
          opacity = 0;
        } else if (time < introEnd) {
          const p = easeScaleIn(
            (time - introStart) / Math.max(1, introEnd - introStart),
          );
          scale = 0.4 + 0.6 * p;
          opacity = p;
        } else if (time >= outroEnd) {
          scale = 0.88;
          opacity = 0;
        } else if (time >= outroStart) {
          const p = easeOutCubic(
            (time - outroStart) / Math.max(1, outroEnd - outroStart),
          );
          scale = 1 - 0.12 * p;
          opacity = 1 - p;
        }
        stage.style.transform = `scale(${scale.toFixed(4)})`;
        stage.style.opacity = opacity.toFixed(3);
      }

      /*
       * Each connector draws out from under its chip while the card it labels
       * slides in, so the line lands exactly as that screen settles. All of
       * them retract together as the deck leaves.
       */
      linkRefs.current.forEach((node, index) => {
        if (!node) return;
        const chip = safeChips[index];
        const owner = chip && schedule[chip.step];
        let drawn;

        if (time >= outroStart) {
          const r = clamp01(
            (time - outroStart) / Math.max(1, seg.outro * 0.75),
          );
          drawn = 1 - r * r;
        } else if (owner && owner.slideStart !== null) {
          drawn = easeOutCubic(
            clamp01((time - owner.slideStart) / Math.max(1, seg.slide)),
          );
        } else {
          // No card of its own — draw during the intro instead.
          const start = introEnd + index * seg.linkStagger;
          drawn = easeOutCubic(
            clamp01((time - start) / Math.max(1, seg.linkDraw)),
          );
        }

        /*
         * The line is grown by moving its end point rather than by animating
         * stroke-dashoffset: with pathLength, non-scaling-stroke and a
         * preserveAspectRatio="none" viewBox all in play, dash geometry is not
         * dependable, while an end point is exact — at drawn === 1 it IS the
         * target, so the line always finishes completely filled.
         */
        const x2 = chip.fromX + (chip.toX - chip.fromX) * drawn;
        const y2 = chip.fromY + (chip.toY - chip.fromY) * drawn;
        const key = `${x2.toFixed(3)},${y2.toFixed(3)}`;
        if (linkOffsets.current[index] !== key) {
          linkOffsets.current[index] = key;
          node.setAttribute('x2', x2.toFixed(3));
          node.setAttribute('y2', y2.toFixed(3));
        }
      });

      let topmost = 0;

      for (let i = 0; i < schedule.length; i += 1) {
        const step = schedule[i];
        const next = schedule[i + 1];
        const slot = slotRefs.current[i];
        const ghost = ghostRefs.current[i];

        let offset = 0;
        let opacity = 0;

        if (i === 0) {
          opacity = time >= introStart ? 1 : 0;
        } else if (time >= step.slideStart) {
          // p: 0 at the lifted start, 1 at rest.
          const p = slideProgress(
            time - step.slideStart,
            seg.slide,
            seg.settle,
          );
          offset = p - 1;
          opacity = clamp01(
            (time - step.slideStart) / Math.max(1, seg.slide * 0.18),
          );
        }

        // The outgoing card crossfades away beneath the arriving one.
        if (next && time >= next.slideStart) {
          const fade = clamp01(
            (time - next.slideStart) / Math.max(1, seg.slide * 0.75),
          );
          opacity = Math.min(opacity, 1 - fade);
        }

        if (time >= outroEnd || time < introStart) opacity = 0;

        if (slot) {
          slot.style.transform = `translate(-50%, -50%) translateY(calc(${offset.toFixed(
            4,
          )} * var(--iso-lift)))`;
          slot.style.opacity = opacity.toFixed(3);
          slot.style.willChange = opacity > 0 ? 'transform, opacity' : 'auto';
        }

        if (ghost) {
          let gOpacity = 0;
          let gDrift = 0;
          if (step.ghostStart !== null) {
            const window_ = Math.max(1, step.slideStart - step.ghostStart);
            if (time >= step.ghostStart && time < step.slideStart) {
              const p = (time - step.ghostStart) / window_;
              gOpacity = clamp01(p * 4);
              gDrift = GHOST_DRIFT * p;
            }
          }
          if (time >= outroStart) gOpacity = 0;
          ghost.style.opacity = gOpacity.toFixed(3);
          ghost.style.transform = `translate(-50%, -50%) translateY(calc(${(
            gDrift - 1
          ).toFixed(4)} * var(--iso-lift)))`;
          ghost.style.willChange = gOpacity > 0 ? 'opacity, transform' : 'auto';
        }

        if (opacity > 0.5) topmost = i;
      }

      setActiveIndex((prev) => (prev === topmost ? prev : topmost));

      if (scrubRef.current && document.activeElement !== scrubRef.current) {
        scrubRef.current.value = String(
          Math.round((time / safeDuration) * 1000),
        );
      }
    },
    [timeline, safeDuration],
  );

  const useStaticFallback = imageLoadFailed;
  const running =
    isPlaying &&
    imagesReady &&
    inView &&
    tabVisible &&
    !useStaticFallback;

  useEffect(() => {
    if (useStaticFallback || !imagesReady) return undefined;

    if (!running) {
      lastTsRef.current = null;
      applyFrame(elapsedRef.current % safeDuration);
      return undefined;
    }

    const tick = (ts) => {
      const last = lastTsRef.current;
      lastTsRef.current = ts;
      if (last !== null) elapsedRef.current += ts - last;

      const cycle = Math.floor(elapsedRef.current / safeDuration);
      if (cycle !== cycleRef.current) {
        cycleRef.current = cycle;
        if (onCycleCompleteRef.current) onCycleCompleteRef.current(cycle);
      }

      applyFrame(elapsedRef.current % safeDuration);
      rafRef.current = window.requestAnimationFrame(tick);
    };

    rafRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
    };
  }, [running, useStaticFallback, imagesReady, applyFrame, safeDuration]);

  const handleScrub = useCallback(
    (event) => {
      const next = (Number(event.target.value) / 1000) * safeDuration;
      elapsedRef.current =
        Math.floor(elapsedRef.current / safeDuration) * safeDuration + next;
      applyFrame(next);
    },
    [applyFrame, safeDuration],
  );

  const cycleBackground = useCallback(() => {
    setBg((prev) => {
      const idx = BACKGROUND_ORDER.indexOf(prev);
      return BACKGROUND_ORDER[(idx + 1) % BACKGROUND_ORDER.length];
    });
  }, []);

  const handleStaticPosterError = useCallback((event) => {
    const image = event.currentTarget;
    if (image.dataset.backupApplied === 'true') return;
    image.dataset.backupApplied = 'true';
    image.src = STATIC_POSTER_BACKUP_SRC;
  }, []);

  // Only the colour is per-card; the slab fill is expressed in CSS in terms of
  // --iso-extrude so it stays correct at every size.
  const sideStyles = useMemo(
    () =>
      steps.map((card) => ({
        '--iso-side-color': card.sideColor || '#1A1A6B',
      })),
    [steps],
  );

  // Pre-measurement size, shared by every chip; refined once after mount.
  const chipFontBase = useMemo(
    () => uniformChipFontScale(safeChips),
    [safeChips],
  );

  const activeLabel = steps[activeIndex]?.alt || 'Quickvee platform';
  const rootClass = [
    styles.root,
    BACKGROUND_CLASS[bg] || BACKGROUND_CLASS.transparent,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (useStaticFallback) {
    return (
      <div ref={rootRef} className={rootClass}>
        <div className={styles.staticViewport}>
          <img
            className={styles.staticPoster}
            src={STATIC_POSTER_SRC}
            alt={STATIC_POSTER_ALT}
            width="2048"
            height="1536"
            draggable="false"
            onError={handleStaticPosterError}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={rootClass}
      role="img"
      aria-label={`Quickvee platform showcase — ${activeLabel}`}
    >
      <div className={styles.viewport}>
        <div className={styles.field}>
          <div ref={stageRef} className={styles.stage}>
            <svg
              className={styles.links}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              {safeChips.map((chip, index) => (
                <line
                  key={chip.id}
                  ref={(node) => {
                    linkRefs.current[index] = node;
                  }}
                  className={styles.link}
                  vectorEffect="non-scaling-stroke"
                  x1={chip.fromX}
                  y1={chip.fromY}
                />
              ))}
            </svg>

            {steps.map((card, index) => {
              return (
                <React.Fragment key={`${card.src}-${index}`}>
                  {index > 0 && (
                    <div
                      ref={(node) => {
                        ghostRefs.current[index] = node;
                      }}
                      className={`${styles.slot} ${styles.ghostSlot}`}
                      aria-hidden="true"
                    >
                      <div className={styles.slab}>
                        <div className={styles.ghostFace} />
                      </div>
                    </div>
                  )}

                  <div
                    ref={(node) => {
                      slotRefs.current[index] = node;
                    }}
                    className={styles.slot}
                    aria-hidden="true"
                  >
                    <div className={styles.slab}>
                      <div className={styles.side} style={sideStyles[index]} />
                      <img
                        className={styles.face}
                        src={card.src}
                        alt=""
                        draggable="false"
                      />
                    </div>
                  </div>
                </React.Fragment>
              );
            })}

            {safeChips.map((chip, index) => (
              <span
                key={chip.id}
                className={styles.chip}
                style={{ left: `${chip.x}%`, top: `${chip.y}%` }}
                aria-hidden="true"
              >
                <span className={styles.chipSlab}>
                  <span
                    ref={(node) => {
                      chipLabelRefs.current[index] = node;
                    }}
                    className={styles.chipLabel}
                    style={{
                      '--iso-chip-font': `calc(var(--iso-chip-size) * ${chipFontBase})`,
                    }}
                  >
                    {chip.label}
                  </span>
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {showControls && (
        <div className={styles.controls}>
          <button
            type="button"
            className={styles.controlButton}
            onClick={() => setIsPlaying((p) => !p)}
            aria-label={
              isPlaying ? 'Pause showcase animation' : 'Play showcase animation'
            }
          >
            {isPlaying ? 'Pause' : 'Play'}
          </button>
          <input
            ref={scrubRef}
            type="range"
            className={styles.scrub}
            min="0"
            max="1000"
            defaultValue="0"
            onChange={handleScrub}
            aria-label="Scrub showcase timeline"
          />
          <button
            type="button"
            className={styles.controlButton}
            onClick={cycleBackground}
            aria-label={`Background: ${bg}. Switch background.`}
          >
            {bg}
          </button>
        </div>
      )}

      <p className={styles.visuallyHidden}>{activeLabel}</p>
    </div>
  );
};

export default IsometricShowcase;
